/// <amd-module name="Wit/UI/Components/WorkItemTreeGrid/WorkItemTreeGrid" />
import * as React from "react";

import { ObservableValue, IObservableValue, IReadonlyObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { Intersection } from "VSSUI/Intersection";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SimpleTableCell, ColumnFill } from "VSSUI/Table";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";

import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { DEFAULT_WORKITEM_PAGE_SIZE } from "Wit/UI/Data";
import { WorkItemGridCell } from "Wit/UI/WorkItemGridCell";
import { WorkItem, WorkItemLink, QueryType, FieldType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";

import { IWorkItemTreeGridColumn, IWorkItemTreeGridProps } from "./WorkItemTreeGrid.props";
import { PagedWorkItemsTreeProvider } from "../../ItemProviders/PagedWorkItemsTreeProvider";
import { getFieldTypesAsync } from "../../DataSources/CollectionFieldsDataSource";

export interface IWorkItemTreeGridState {
    isFirstPageLoaded: boolean;
    areFieldsLoaded: boolean;
    columns: ITreeColumn<WorkItem>[];
}

/**
 * Component to display a list of work items as tree nodes.
 */
export class WorkItemTreeGrid extends VssComponent<IWorkItemTreeGridProps, IWorkItemTreeGridState> {

    // Expandable columns
    private expandTitle = false;
    private expandId = false;

    private treeItemProvider: PagedWorkItemsTreeProvider<WorkItem>;
    private fieldsMap: { [key: string]: FieldType } | undefined;

    // Dictionary of work item ids mapped to their relations.
    private workItemLinksDictionary: { [id: number]: number[] };

    private throttleColumnResize: (() => void) | null;
    private timerManagement: TimerManagement;

    private static treeGridClassName = "work-item-tree-grid";

    constructor(props: IWorkItemTreeGridProps) {
        super(props);

        this.timerManagement = new TimerManagement();

        this.state = {
            isFirstPageLoaded: false,
            areFieldsLoaded: false,
            columns: this.buildColumns(this.props.columns)
        };

        this.initialize(this.props);
    }

    public render(): JSX.Element | null {
        const { showLines } = this.props;

        return this.state.areFieldsLoaded ?
            <Intersection>
                <div className={`wit-ui-control ${WorkItemTreeGrid.treeGridClassName}`}>
                    <Tree<WorkItem | IReadonlyObservableValue<WorkItem | undefined>>
                        itemProvider={this.treeItemProvider}
                        columns={this.state.columns}
                        showLines={showLines ? showLines : false}
                        onToggle={this.onToggle}
                    />
                </div>
            </Intersection> :
            <Spinner className="absolute-fill" size={SpinnerSize.medium} />;
    }

    public componentDidUpdate(prevProps: IWorkItemTreeGridProps) {
        if (this.didPropsChanged(prevProps, this.props)) {
            this.initialize(this.props);

            // Put updated columns into state to kick off rendering process
            const builtColumns = this.buildColumns(this.props.columns);
            this.setState({
                columns: builtColumns
            }, () => this.loadFirstPage());
        }
    }

    async componentDidMount(): Promise<void> {
        // Wait to download field data so that buildColumns has updated info
        const fieldsPromise = this.trackPromise(getFieldTypesAsync(this.context.pageContext));
        this.fieldsMap = await fieldsPromise.promise;

        // Rebuild columns to update ones that are dependent on fields.
        const builtColumns = this.buildColumns(this.props.columns);
        this.setState({
            areFieldsLoaded: true,
            columns: builtColumns
        }, () => this.loadFirstPage())
    }

    /**
     * When we get new props, we need to re-initialize the columns and the provider.
     */
    private initialize(props: IWorkItemTreeGridProps): void {
        const { itemsSource, dataProvider } = props;

        this.setExpandableColumn();
        this.updateThrottleColumnResize();
        this.buildWorkItemLinksDictionary();
        this.treeItemProvider = new PagedWorkItemsTreeProvider<WorkItem>(itemsSource, this.mapWorkItemsToTree, dataProvider, props.pageSize ?? DEFAULT_WORKITEM_PAGE_SIZE);
    }

    private async loadFirstPage(): Promise<void> {
        // preload the first page if it's not already available
        if (!this.treeItemProvider.isFirstPageReady()) {
            await this.treeItemProvider.ensureFirstPage();
            this.setState({
                isFirstPageLoaded: true
            });
        }
    }

    /**
     * Construct a tree node for a given work item.
     * @param workItem WorkItem
     */
    private mapWorkItemsToTree = (
        workItems: readonly (WorkItem | null)[] | null,
        { isRootNode = false }: { isRootNode?: boolean } = {}
    ): ITreeItem<WorkItem | IReadonlyObservableValue<WorkItem | undefined>>[] => {
        return workItems ? workItems.map(workItem => {
            const emptyTreeItem = {
                data: new ObservableValue<WorkItem | undefined>(undefined)
            } as ITreeItem<WorkItem | IReadonlyObservableValue<WorkItem | undefined>>;

            if (!workItem) {
                return emptyTreeItem;
            } else {
                return {
                    childItems: this.getChildItemsForWorkItem(workItem, isRootNode),
                    expanded: false,
                    data: workItem
                };
            }
        }) : [];
    }

    private getChildItemsForWorkItem(
        workItem: WorkItem,
        isRootNode: boolean
    ): ITreeItem<ObservableValue<undefined>>[] | undefined {
        if (this.props.queryType === QueryType.Flat ||
            this.props.queryType === QueryType.OneHop && !isRootNode ||
            !this.hasChildren(workItem)) {
            return undefined;
        }

        const childItems: ITreeItem<ObservableValue<undefined>>[] = [];
        const childItemsLimit = this.workItemLinksDictionary[workItem.id].length < DEFAULT_WORKITEM_PAGE_SIZE ?
            this.workItemLinksDictionary[workItem.id].length :
            DEFAULT_WORKITEM_PAGE_SIZE;

        for (let i = 0; i < childItemsLimit; i++) {
            childItems.push({ data: new ObservableValue<undefined>(undefined) });
        }

        return childItems;
    }

    /**
     * Build a dictionary of parent workItemIds mapped to lists of their children's workItemIds.
     */
    public buildWorkItemLinksDictionary() {
        const { workItemRelations } = this.props;
        this.workItemLinksDictionary = {};
        if (workItemRelations) {
            workItemRelations.forEach((relation: WorkItemLink) => {
                if (relation.source) {
                    if (!this.workItemLinksDictionary[relation.source.id]) {
                        this.workItemLinksDictionary[relation.source.id] = [];
                    }

                    this.workItemLinksDictionary[relation.source.id].push(relation.target.id);
                }
            });
        }
    }

    /**
     * Determines which column should contain expandable rows.
     */
    public setExpandableColumn(): void {
        const { workItemRelations, columns } = this.props;

        if (workItemRelations) {
            columns.forEach((column: IWorkItemTreeGridColumn) => {
                /**
                 * If title column is present, it is expandable. If title isn't present,
                 * then the Id column is expandable.
                 */
                if (column.fieldName === CoreFieldRefNames.Id) {
                    this.expandId = true;
                }

                if (column.fieldName === CoreFieldRefNames.Title) {
                    this.expandTitle = true;
                    this.expandId = false;
                }
            });
        }
    }

    private updateThrottleColumnResize() {
        if (this.props.columns.some((col: IWorkItemTreeGridColumn) => col.fieldName === CoreFieldRefNames.Tags)) {
            // We need to force update on column resize so that the Tags component gets rerendered.
            this.throttleColumnResize = this.timerManagement.throttle(() => {
                /**
                 * The VSSUI/List component underlies WorkItemTreeGrid. It creates an HTML table and although it recalculates the width, it 
                 * simply reuses the rows that have previously been rendered. Since the rows don't get rendered again after an update, 
                 * the cells containing TagsList don't get rerendered and the display of the tags never changes. 
                 * On line 109 of List.tsx, if the itemProvider for the List changes, the List will dump the previously rendered rows 
                 * and rerender the rows. Rerendering the rows rerenders the cells rerenders the TagsList which is needed to update
                 * the tag view on resize.
                 * The same underlying data provider is used when constructing a new PagedWorkItemsTreeProvider - meaning that 
                 * the below only amounts to the cost of creating a new object.
                 * TL;DR changing the treeItemProvider is cheap and forces the underlying component to rerender the TagsList
                 */
                this.treeItemProvider = new PagedWorkItemsTreeProvider<WorkItem>(this.props.itemsSource, this.mapWorkItemsToTree, this.props.dataProvider, DEFAULT_WORKITEM_PAGE_SIZE);
                this.forceUpdate();
            }, 200);
        } else {
            this.throttleColumnResize = null;
        }
    }

    private onToggle = (event: any, treeItem: ITreeItemEx<WorkItem>) => {
        this.treeItemProvider.onToggle(treeItem, this.workItemLinksDictionary[treeItem.underlyingItem.data.id]);
    };

    private didPropsChanged(prevProps: IWorkItemTreeGridProps, currentProps: IWorkItemTreeGridProps): boolean {
        return prevProps.itemsSource !== currentProps.itemsSource || prevProps.dataProvider !== currentProps.dataProvider;
    }

    /**
     * Returns true if the work item has children.
     * @param workItem WorkItem
     */
    private hasChildren(workItem: WorkItem): boolean {
        return workItem && !!this.workItemLinksDictionary[workItem.id];
    }

    private buildColumns(workItemTreeGridColumns: IWorkItemTreeGridColumn[]): ITreeColumn<WorkItem>[] {
        const columns = workItemTreeGridColumns.map(this.buildColumn);
        // To ensure the tree will take the full width of the available space, we need a small non fixed sized
        // column at the end.
        columns.push({
            ...ColumnFill,
            width: -0.01
        });

        // Find the column with the highest max width, and set it's
        // width to -100.  This will simulate that column to grow to
        // fill all available space.
        let foundMaxWidth = false;
        let maxWidthIndex = 0;
        let maxWidth = 0;
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            if (column.maxWidth! > maxWidth) {
                foundMaxWidth = true;
                maxWidth = column.maxWidth!;
                maxWidthIndex = i;
            }
        }

        if (foundMaxWidth) {
            if (workItemTreeGridColumns[maxWidthIndex].isResizable) {
                (columns[maxWidthIndex].width as ObservableValue<number>) = new ObservableValue(-100);
            } else {
                columns[maxWidthIndex].width = -100;
            }
        }

        return columns;
    };

    private readonly buildColumn = (workItemTreeGridColumn: IWorkItemTreeGridColumn): ITreeColumn<WorkItem> => {
        const { name, fieldName, minWidth, maxWidth, width, isResizable, onRender } = workItemTreeGridColumn;

        const cellRenderer = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: ITreeColumn<WorkItem>,
            treeItem: ITreeItemEx<WorkItem>): JSX.Element => {
            if (!treeItem || !treeItem.underlyingItem.data) {
                return null!;
            }

            const fieldType = this.fieldsMap && this.fieldsMap[fieldName];
            const cellRenderer = onRender ?
                onRender(treeItem.underlyingItem.data, columnIndex, workItemTreeGridColumn)
                : this.defaultCellRenderer(treeItem.underlyingItem.data, fieldName, fieldType)!;

            return this.isColumnHierarchical(workItemTreeGridColumn) ?
                <ExpandableTreeCell
                    key={columnIndex}
                    columnIndex={columnIndex}
                    treeItem={treeItem}
                    treeColumn={column}
                >
                    {cellRenderer}
                </ExpandableTreeCell> :
                <SimpleTableCell
                    key={columnIndex}
                    columnIndex={columnIndex}
                    tableColumn={column}
                >
                    {cellRenderer}
                </SimpleTableCell>;
        }

        const onSize = (event: MouseEvent, columnIndex: number, width: number, column: ITreeColumn<WorkItem>): void => {
            // Eventual updateThrottleColumnResize calls forceUpdate, so we don't need state tracking
            // and can update state directly. 
            (this.state.columns[columnIndex].width as ObservableValue<number>).value = width;
            if (this.throttleColumnResize) {
                this.throttleColumnResize();
            }
        };

        const column: ITreeColumn<WorkItem> = {
            id: "col-" + fieldName,
            name: name || fieldName,
            className: "work-item-grid-column",
            headerClassName: "work-item-grid-column-header",
            minWidth: minWidth,
            maxWidth: maxWidth,
            width: width || maxWidth || minWidth,
            onSize: isResizable ? onSize : undefined,
            renderCell: cellRenderer
        };

        if (this.isColumnHierarchical(workItemTreeGridColumn)) {
            column.hierarchical = true;
            column.indentationSize = 20;
            column.headerClassName && column.headerClassName.concat(" expanded-column");
        }

        return column;
    };

    /**
     * Returns true if the given column is hierarchical.
     */
    private isColumnHierarchical(workItemTreeGridColumn: IWorkItemTreeGridColumn) {
        return ((workItemTreeGridColumn.fieldName === CoreFieldRefNames.Id) && this.expandId) || ((workItemTreeGridColumn.fieldName === CoreFieldRefNames.Title) && this.expandTitle);
    }

    private defaultCellRenderer(workItem: WorkItem | null, fieldName: string, fieldType: FieldType | undefined): JSX.Element | null {
        const { dataProvider } = this.props;

        return <WorkItemGridCell
            workItem={workItem}
            dataProvider={dataProvider}
            fieldName={fieldName}
            fieldType={fieldType}
        />;
    }
}